/**
 * Barra de cuota del espacio activo.
 *
 * Un espacio compartido sin pagar no es un plan Gratis: está bloqueado hasta
 * activar sus 50 GB. Separar ese estado evita prometer caducidad de 7 días,
 * mostrar 0/0 o enviar al usuario a comprar Pro por segunda vez.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { formatBytes, getCheckout, type QuotaInfo } from '@/services/billingService'
import type { SpaceListItem } from '@/services/spacesService'

interface Props {
  quota: QuotaInfo
  space: SpaceListItem | null
}

export function StorageBar({ quota, space }: Props) {
  const { t } = useTranslation()
  const [activating, setActivating] = useState(false)
  const [failed, setFailed] = useState(false)

  async function activateSpace() {
    if (!space || space.isPersonal || activating) return
    setActivating(true)
    setFailed(false)
    try {
      const checkout = await getCheckout('space_base', { spaceId: space.id })
      if (checkout.action !== 'checkout') {
        window.location.assign('/?spaceActivated=1')
        return
      }
      if (!checkout.url) throw new Error('checkout_without_url')
      window.location.assign(checkout.url)
    } catch {
      setActivating(false)
      setFailed(true)
    }
  }

  if (quota.needsActivation) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-3 space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-medium text-zinc-900">{t('panel.espacioSinActivar')}</p>
          <button
            type="button"
            disabled={activating || space?.role !== 'admin'}
            onClick={() => void activateSpace()}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-900 underline underline-offset-4 hover:no-underline disabled:opacity-50"
          >
            {activating && <Loader2 className="h-3 w-3 animate-spin" />}
            {t('panel.activarEspacio')}
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 leading-snug">
          {failed ? t('panel.activarError') : t('panel.activarPista')}
        </p>
      </div>
    )
  }

  const pct = quota.bytesLimit > 0 ? Math.min(100, (quota.bytesUsed / quota.bytesLimit) * 100) : 0
  const warning = pct >= 80
  const danger = pct >= 95
  const label = quota.isPaidSpace
    ? t('panel.planEspacio')
    : quota.plan === 'pro'
      ? t('panel.planPro')
      : t('panel.planGratis')
  const pricesUrl = quota.isPaidSpace && space
    ? `/precios?spaceId=${encodeURIComponent(space.id)}`
    : '/precios'

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium text-zinc-900">
          {t('panel.almacenamiento')} <span className="text-zinc-500 font-normal">· {label}</span>
        </p>
        <p className="text-xs text-zinc-500">{formatBytes(quota.bytesUsed)} / {formatBytes(quota.bytesLimit)}</p>
      </div>
      <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full ${danger ? 'bg-red-500' : warning ? 'bg-amber-500' : 'bg-zinc-900'}`} style={{ width: `${pct}%` }} />
      </div>
      {(quota.plan === 'free' || warning || quota.ownerProMissing) && (
        <div className="flex items-center justify-between pt-0.5">
          <p className="text-[11px] text-zinc-500 leading-snug">
            {quota.ownerProMissing ? t('panel.requierePro') : quota.plan === 'free' ? t('panel.caducaGratis') : danger ? t('panel.casiSinEspacio') : t('panel.cercaLimite')}
          </p>
          <Link to={pricesUrl} className="text-[11px] font-medium text-zinc-900 underline underline-offset-4 ml-2">
            {quota.plan === 'pro' ? t('panel.ctaMas') : t('panel.ctaPro')}
          </Link>
        </div>
      )}
    </div>
  )
}
